// components
import { FormControlLabel } from '@mui/material';

// constants
import { NOTE_VARIANT } from '../Notes.consts';

// hooks
import { useState } from 'react';
import useAddEmployeeNote from '../hooks/useAddEmployeeNote';
import useCreateDutyLineNote from 'components/employee-card/employment/hours-table/hooks/useCreateDutyLineNote';
import useAddRequestNote from 'components/flex-portal/FlexHistory/hooks/useAddRequestNote';

// styles
import {
  StyledBoxFlexCenterSpaceBetween,
  StyledBoxFlexColumnNoGap
} from 'components/shared/Box/Box.styles';
import {
  StyledCompactCheckboxHeight,
  StyledNoteFormButton,
  StyledNotesTextField
} from './NoteForm.styles';

// types
import { NoteFormProps } from './NoteForm.types';
import { NotesTypeEnums } from '../Notes.types';

// utils
import { t } from 'i18next';

const NoteForm = ({
  employeeId,
  setShowNoteForm,
  type,
  dutyLineId,
  isEmployeePortal = false,
  isSavedByParent = false,
  onAddNote,
  noteVariant = NOTE_VARIANT.DEFAULT,
  isCompact = false,
  requestId,
  isWebSocketEnabled
}: NoteFormProps) => {
  const [noteContent, setNoteContent] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [isExternal, setIsExternal] = useState(false);
  const { mutateAsync: createEmployeeNote, isLoading: isLoadingEmployeeNote } =
    useAddEmployeeNote();
  const { mutateAsync: createDutyLineNote, isLoading: isLoadingDutyLineNote } =
    useCreateDutyLineNote();

  const { mutateAsync: createRequestNote, isLoading: isLoadingRequestNote } =
    useAddRequestNote({ isWebSocketEnabled });

  const resetForm = () => {
    setNoteContent('');
    setShowNoteForm(false);
    setIsPrivate(false);
  };

  const handleSave = () => {
    if (!noteContent.trim()) return;

    if (isSavedByParent && onAddNote) {
      const currentEmployee = sessionStorage.getItem('employee');
      const currentEmployeeParsed = currentEmployee
        ? JSON.parse(currentEmployee)
        : null;
      onAddNote({
        id: Date.now(),
        content: noteContent,
        flags: isPrivate ? ['PRIVATE'] : [],
        creationUser: currentEmployeeParsed?.nameCompound || '',
        creationDate: new Date().toISOString()
      });
      resetForm();
      return;
    }

    if (type === NotesTypeEnums.FLEX_MATCH_REQUEST) {
      if (!requestId) return;

      createRequestNote(
        {
          body: {
            note: {
              content: noteContent,
              flags:
                (isPrivate && ['PRIVATE']) ||
                (isExternal && ['EXTERNAL']) ||
                [],
              refId: requestId
            }
          }
        },
        {
          onSuccess: resetForm
        }
      );
      return;
    }

    if (!employeeId) return;

    if (type === NotesTypeEnums.DUTY_LINE && dutyLineId) {
      createDutyLineNote(
        {
          employeeId,
          isEmployeePortal,
          dutyLineId,
          body: {
            content: noteContent,
            flags: isPrivate ? ['PRIVATE'] : []
          }
        },
        {
          onSuccess: resetForm
        }
      );
    } else {
      createEmployeeNote(
        {
          employeeId,
          body: {
            content: noteContent
          }
        },
        {
          onSuccess: resetForm
        }
      );
    }
  };

  return (
    <>
      <StyledNotesTextField
        minRows={isCompact ? 2 : 6}
        multiline
        value={noteContent}
        onChange={(e) => setNoteContent(e.target.value)}
        noteVariant={noteVariant}
      />
      <StyledBoxFlexCenterSpaceBetween>
        <StyledBoxFlexColumnNoGap>
          {type === NotesTypeEnums.DUTY_LINE ||
            (type === NotesTypeEnums.FLEX_MATCH_REQUEST && (
              <FormControlLabel
                control={
                  <StyledCompactCheckboxHeight
                    name="isPrivate"
                    checked={isPrivate}
                    onChange={(evt, checked) => setIsPrivate(checked)}
                  />
                }
                label={t('noteIsPrivate')}
              />
            ))}
          {type === NotesTypeEnums.FLEX_MATCH_REQUEST && (
            <FormControlLabel
              control={
                <StyledCompactCheckboxHeight
                  name="visibleForExternal"
                  checked={isExternal}
                  onChange={(evt, checked) => setIsExternal(checked)}
                />
              }
              label={t('noteIsVisibleForExternal')}
            />
          )}
        </StyledBoxFlexColumnNoGap>
        <StyledNoteFormButton
          onClick={handleSave}
          color="primary"
          variant="contained"
          size={isCompact ? 'small' : 'medium'}
          disabled={
            isLoadingEmployeeNote ||
            isLoadingDutyLineNote ||
            isLoadingRequestNote
          }
        >
          {isSavedByParent ? t('add') : t('save')}
        </StyledNoteFormButton>
      </StyledBoxFlexCenterSpaceBetween>
    </>
  );
};

export default NoteForm;
